import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { UserRoleEnum } from "@custom-types/member-types";
import { fetchCompanyMemberGroupDetails, fetchCompanyMemberGroups, refreshCompanyMemberGroup } from "@store/company-member-groups/company-member-groups-thunk";

/** Entity stored per group in the adapter */

/** Creates an entity adapter to store a map with all the group details that belong to a company member */
export const companyMemberGroupDetailsAdapter = createEntityAdapter({
  selectId: group => group.id
});

/**
 * State company member groups.
 */

const initialState = {
  ...companyMemberGroupDetailsAdapter.getInitialState(),
  status: "uninitialized",
  groupDetailsStatus: {},
  projectsMap: {},
  memberIdentity: null,
  groupRequestIds: {}
};

/** Whether the details of a group were fetched for another member than the one whose groups are stored */
function isOfAnotherMember(state, userIdentity) {
  return state.memberIdentity !== userIdentity;
}

/** The request for a group, its details or a refresh of it, as the thunks tell it */

/** Remembers a request for a group as the latest one, unless it is for another member */
function startGroupRequest(state, {
  meta
}) {
  if (isOfAnotherMember(state, meta.arg.userIdentity)) {
    return false;
  }
  state.groupRequestIds[meta.arg.groupId] = meta.requestId;
  return true;
}

/**
 * Whether the response of a request for a group may change the group: it is for the member whose
 * groups are stored, and no newer request for the group started since
 */
function isLatestGroupRequest(state, {
  meta
}) {
  return !isOfAnotherMember(state, meta.arg.userIdentity) && state.groupRequestIds[meta.arg.groupId] === meta.requestId;
}

/** Removes a group from the state, together with its projects */
function removeGroupFromState(state, groupId) {
  const group = state.entities[groupId];
  if (group?.projectIds) {
    for (const projectId of group.projectIds) {
      delete state.projectsMap[projectId];
    }
  }
  delete state.groupDetailsStatus[groupId];
  delete state.groupRequestIds[groupId];
  companyMemberGroupDetailsAdapter.removeOne(state, groupId);
}

/**
 * Slice to access state of loaded company member groups
 */
const companyMemberGroupsSlice = createSlice({
  name: "companyMemberGroups",
  initialState,
  reducers: {
    /**
     * Resets the state of the company member groups slice.
     */
    resetCompanyMemberGroupsState: () => initialState,
    /** Removes a single group from the adapter by ID */
    removeGroup(state, action) {
      removeGroupFromState(state, action.payload);
    },
    /** Removes projects from a group by their IDs */
    removeProjectsFromGroup(state, action) {
      const {
        groupId,
        projectIds: idsToRemove
      } = action.payload;
      const group = state.entities[groupId];
      if (group?.projectIds) {
        companyMemberGroupDetailsAdapter.updateOne(state, {
          id: groupId,
          changes: {
            projectIds: group.projectIds.filter(id => !idsToRemove.includes(id))
          }
        });
      }
      for (const projectId of idsToRemove) {
        delete state.projectsMap[projectId];
      }
    },
    /** Updates the role of a project in the projects map */
    updateProjectRoleInMap(state, action) {
      const {
        projectId,
        newRole
      } = action.payload;
      const project = state.projectsMap[projectId];
      if (!project) {
        return;
      }
      // A team grants a role only when the current effective role outranks the
      // explicit assignment; that team role is unaffected by this direct change.
      const teamRole = project.role && project.explicitRole && UserRoleEnum[project.role] > UserRoleEnum[project.explicitRole] ? project.role : undefined;
      project.explicitRole = newRole;

      // The effective role stays the higher of the new direct role and any team role.
      project.role = teamRole && UserRoleEnum[teamRole] > UserRoleEnum[newRole] ? teamRole : newRole;
    }
  },
  extraReducers(builder) {
    builder.addCase(fetchCompanyMemberGroups.pending, (state, action) => {
      state.status = "pending";
      state.memberIdentity = action.meta.arg.memberId;
      state.requestId = action.meta.requestId;
    }).addCase(fetchCompanyMemberGroups.fulfilled, (state, action) => {
      // An older request, e.g. for the member shown before or from before a reset, arrived last
      if (action.meta.requestId !== state.requestId) {
        return;
      }
      companyMemberGroupDetailsAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    }).addCase(fetchCompanyMemberGroups.rejected, (state, action) => {
      // A request aborted on purpose, e.g. when its page closed, or an older one says nothing
      // about the groups that a newer request may be fetching already
      if (action.meta.aborted || action.meta.requestId !== state.requestId) {
        return;
      }
      state.status = "rejected";
    }).addCase(fetchCompanyMemberGroupDetails.pending, (state, action) => {
      if (startGroupRequest(state, action)) {
        state.groupDetailsStatus[action.meta.arg.groupId] = "pending";
      }
    }).addCase(fetchCompanyMemberGroupDetails.fulfilled, (state, action) => {
      // The details of a group of the member shown before would end up on the group of this one,
      // and older details would undo a change a newer request already brought
      if (!isLatestGroupRequest(state, action)) {
        return;
      }
      const {
        groupId
      } = action.meta.arg;
      const {
        projects,
        projectIds,
        explicitRoles
      } = action.payload;
      companyMemberGroupDetailsAdapter.updateOne(state, {
        id: groupId,
        changes: {
          projectIds,
          explicitRoles
        }
      });
      for (const project of projects) {
        state.projectsMap[project.id] = project;
      }
      state.groupDetailsStatus[groupId] = "succeeded";
    }).addCase(fetchCompanyMemberGroupDetails.rejected, (state, action) => {
      // Aborted on purpose, e.g. when its dialog closed, which says nothing about the group, nor
      // does a request for another member or an older one
      if (action.meta.aborted || !isLatestGroupRequest(state, action)) {
        return;
      }
      state.groupDetailsStatus[action.meta.arg.groupId] = "rejected";
    }).addCase(refreshCompanyMemberGroup.pending, (state, action) => {
      startGroupRequest(state, action);
    }).addCase(refreshCompanyMemberGroup.fulfilled, (state, action) => {
      const {
        groupId,
        group
      } = action.payload;
      const storedGroup = state.entities[groupId];

      // The groups were reset meanwhile, e.g. since the dialog that changed them closed, they
      // are the groups of another member now, or a newer request for the group started since
      if (!storedGroup || !isLatestGroupRequest(state, action)) {
        return;
      }
      if (!group) {
        removeGroupFromState(state, groupId);
        return;
      }
      for (const projectId of storedGroup.projectIds ?? []) {
        delete state.projectsMap[projectId];
      }
      const {
        projects,
        ...entity
      } = group;
      companyMemberGroupDetailsAdapter.setOne(state, entity);
      for (const project of projects) {
        state.projectsMap[project.id] = project;
      }
      state.groupDetailsStatus[groupId] = "succeeded";
    });
  }
});
export const {
  resetCompanyMemberGroupsState,
  removeGroup,
  removeProjectsFromGroup,
  updateProjectRoleInMap
} = companyMemberGroupsSlice.actions;
export const companyMemberGroupsReducer = companyMemberGroupsSlice.reducer;